import React, {
	type Context,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';

import type { ResultType } from '../../../utils/quick-find/analytics';
import {
	isHeadingItem,
	isPeopleAndTeamsItem,
	isRecentActivityItem,
	isRecommendedDocumentItem,
	isSearchResultItem,
	type Item,
} from '../types';

const CONTENT_SECTION_RESULT_TYPES: readonly ResultType[] = [
	'searchResults',
	'recommendations',
	'recentActivities',
	'peopleAndTeams',
];

type ItemsContextType = {
	getItemSectionId?: (id: string | undefined, resultType: ResultType) => string | undefined;
	invalidateItem: (id: string) => void;
	getItemIndexes: (id: string | undefined) =>
		| {
				globalIndex: number;
				sectionIndex: number;
				indexWithinSection: number;
				resultCount: number;
				sectionCount: number;
				sectionId: string;
		  }
		| undefined;
};

const ItemsContext: Context<ItemsContextType | undefined> = createContext<
	ItemsContextType | undefined
>(undefined);

const isItemCountable = (item: Item) =>
	isSearchResultItem(item) ||
	isRecommendedDocumentItem(item) ||
	isRecentActivityItem(item) ||
	isPeopleAndTeamsItem(item);

const isHeading = (item: Item) => isHeadingItem(item);

const getItemsAndSectionsCount = (allItems: Item[]) =>
	allItems.reduce(
		({ resultCount, sectionCount }, item, currentIndex) => {
			if (isItemCountable(item)) {
				resultCount++;
			}

			if (currentIndex === 0 && !isHeading(item)) {
				// if the first item is not heading, we consider it in a section
				sectionCount++;
			}

			if (isHeading(item)) {
				sectionCount++;
			}

			return { resultCount, sectionCount };
		},
		{ resultCount: 0, sectionCount: 0 },
	);

export const useItemsAnalyticsContext: () => ItemsContextType = () => {
	const ctx = useContext(ItemsContext);
	if (!ctx) {
		// We throw an error here to make sure the hook is used within the provider,
		// otherwise it would return undefined and cause runtime errors and is hard to debug
		throw new Error('useItemsAnalyticsContext must be used within ItemsAnalyticsProvider');
	}
	return ctx;
};

export const ItemsAnalyticsProvider: React.FC<{
	allItems: Item[];
	includeSectionIds?: boolean;
	children: React.ReactNode;
}> = ({ allItems, includeSectionIds = false, children }) => {
	const invalidatedIdsRef = useRef<Set<string>>(new Set());

	const getItemSectionId = useMemo(() => {
		if (!includeSectionIds) {
			return undefined;
		}

		const sectionIds = new Map<string, string>();
		let sectionId: string | undefined;
		for (const item of allItems) {
			if (isHeadingItem(item) && item.includeInAnalytics !== false) {
				sectionId = item.id;
			} else if (isItemCountable(item) && sectionId) {
				sectionIds.set(item.id, sectionId);
			}
		}

		return (id: string | undefined, resultType: ResultType) => {
			// Query and intent rows move above the content sections in chat convergence.
			if (id && CONTENT_SECTION_RESULT_TYPES.includes(resultType)) {
				return sectionIds.get(id);
			}
			return undefined;
		};
	}, [allItems, includeSectionIds]);

	useEffect(() => {
		// Reset invalidated IDs when allItems change, the items would call invalidateItem again if needed
		invalidatedIdsRef.current.clear();
	}, [allItems]);

	const invalidateItem = (id: string) => {
		invalidatedIdsRef.current.add(id);
	};

	const { resultCount, sectionCount } = useMemo(
		() => getItemsAndSectionsCount(allItems),
		[allItems],
	);

	const getItemIndexes = useCallback(
		(id: string | undefined) => {
			const item =
				id && !invalidatedIdsRef.current.has(id)
					? allItems.find((item) => item.id === id)
					: undefined;
			if (!item || !isItemCountable(item)) {
				return undefined;
			}

			let globalIndex = -1;
			let sectionIndex = -1;
			let indexWithinSection = -1;
			let sectionId = '';

			const activeItems = allItems.filter((item) => !invalidatedIdsRef.current.has(item.id));
			for (let i = 0; i < activeItems.length; i++) {
				if (isHeading(activeItems[i])) {
					sectionIndex++;
					indexWithinSection = -1;
					sectionId = activeItems[i].id;
				} else {
					if (i === 0) {
						// if the first item is not heading, we consider it in a section
						sectionIndex++;
					}
					if (isItemCountable(activeItems[i])) {
						indexWithinSection++;
						globalIndex++;
					}

					if (activeItems[i].id === id) {
						return {
							globalIndex,
							sectionIndex,
							indexWithinSection,
							resultCount,
							sectionCount,
							sectionId,
						};
					}
				}
			}

			// if there is no match, return undefined
			return undefined;
		},
		[allItems, resultCount, sectionCount],
	);

	return (
		<ItemsContext.Provider value={{ invalidateItem, getItemIndexes, getItemSectionId }}>
			{children}
		</ItemsContext.Provider>
	);
};
